import { useState } from "react";
import { Phone, Mail, MapPin, Send, CheckCircle, ArrowRight } from "lucide-react";
import axios from "axios";
import { usePageMeta } from "@/hooks/usePageMeta";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const serviceOptions = ["IT Services","ERP Services","Cloud Services","IT Infrastructure","Cyber Security","Security & Surveillance","Other"];

export default function ContactPage() {
  usePageMeta("Contact Us", "Reach Vaana BuildLogic Technologies for consultations, support, or partnerships. Office No. 7, Ground Floor, Indra Nursery, Near Badarpur Metro Station, New Delhi 110044. Call +91 9953167073 or email info@vaanabuildlogictechnologies.com.");
  const [form, setForm] = useState({ name:"", email:"", phone:"", company:"", service_interest:"", message:"" });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true); setError(""); setSuccess(false);
    try {
      await axios.post(`${API}/contact`, form);
      setSuccess(true);
      setForm({ name:"", email:"", phone:"", company:"", service_interest:"", message:"" });
    } catch { setError("Something went wrong. Please try again or contact us directly."); }
    finally { setLoading(false); }
  };

  const inputClass = "w-full px-4 py-3 bg-white border border-gray-300 rounded-lg text-[#111827] text-sm placeholder-gray-400 focus:outline-none focus:border-[#0D72B8] focus:ring-2 focus:ring-[#0D72B8]/15 transition-all";

  return (
    <div data-testid="contact-page" className="pt-20">
      {/* Hero */}
      <section className="relative py-24 md:py-32 wave-divider overflow-hidden">
        <div className="absolute inset-0 bg-[#0D72B8]" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <p className="text-xs tracking-[0.2em] uppercase text-blue-200 font-semibold mb-4">Contact Us</p>
          <h1 className="text-5xl sm:text-6xl leading-tight font-['Sora'] font-medium text-white mb-6">
            Let's build your next enterprise solution together.
          </h1>
          <p className="text-blue-100 max-w-2xl leading-relaxed">
            Reach Vaana BuildLogic Technologies for consultations, support, or partnerships. We will get back to you within 24 hours.
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-14">
            {/* Form */}
            <div className="lg:col-span-3">
              <div className="p-8 md:p-10 bg-white border border-gray-200 rounded-2xl shadow-sm">
                <h3 className="text-2xl font-['Sora'] font-semibold text-[#111827] mb-2">Send us an enquiry</h3>
                <p className="text-sm text-[#6B7280] mb-8">Fill out the form and we will respond within 24 hours.</p>

                {success && (
                  <div data-testid="contact-success-message" className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
                    <CheckCircle size={18} className="text-green-600" />
                    <p className="text-sm text-green-700">Thank you! Your enquiry has been submitted successfully.</p>
                  </div>
                )}
                {error && (
                  <div data-testid="contact-error-message" className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-600">{error}</p>
                  </div>
                )}

                <form onSubmit={handleSubmit} data-testid="contact-form" className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="text-xs text-[#6B7280] font-medium mb-1.5 block">Full Name *</label>
                      <input type="text" name="name" value={form.name} onChange={handleChange} required data-testid="contact-input-name" className={inputClass} placeholder="John Doe" />
                    </div>
                    <div>
                      <label className="text-xs text-[#6B7280] font-medium mb-1.5 block">Email Address *</label>
                      <input type="email" name="email" value={form.email} onChange={handleChange} required data-testid="contact-input-email" className={inputClass} placeholder="john@company.com" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="text-xs text-[#6B7280] font-medium mb-1.5 block">Phone Number</label>
                      <input type="tel" name="phone" value={form.phone} onChange={handleChange} data-testid="contact-input-phone" className={inputClass} placeholder="+91 XXXXXXXXXX" />
                    </div>
                    <div>
                      <label className="text-xs text-[#6B7280] font-medium mb-1.5 block">Company Name</label>
                      <input type="text" name="company" value={form.company} onChange={handleChange} data-testid="contact-input-company" className={inputClass} placeholder="Your Company" />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-[#6B7280] font-medium mb-1.5 block">Scope of Service</label>
                    <select name="service_interest" value={form.service_interest} onChange={handleChange} data-testid="contact-select-service" className={inputClass}>
                      <option value="">Select a service</option>
                      {serviceOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-xs text-[#6B7280] font-medium mb-1.5 block">Message *</label>
                    <textarea name="message" value={form.message} onChange={handleChange} required rows={5} data-testid="contact-input-message" className={`${inputClass} resize-none`} placeholder="Tell us about your project..." />
                  </div>
                  <button type="submit" disabled={loading} data-testid="contact-submit-button"
                    className="inline-flex items-center gap-2 px-8 py-3.5 btn-solid text-sm font-semibold rounded-full disabled:opacity-50">
                    {loading ? "Submitting..." : "Submit"} <Send size={15} />
                  </button>
                </form>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-2 space-y-6">
              <div className="p-7 bg-[#F9FAFB] border border-gray-200 rounded-2xl">
                <h4 className="text-sm font-semibold text-[#111827] mb-5">Contact Information</h4>
                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <Phone size={17} className="text-[#0D72B8] mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-[#9CA3AF] mb-0.5">Phone</p>
                      <a href="tel:9953167073" data-testid="contact-phone" className="text-[#111827] font-medium text-sm hover:text-[#0D72B8] transition-colors">+91 9953167073</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Mail size={17} className="text-[#0D72B8] mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-[#9CA3AF] mb-0.5">Email</p>
                      <a href="mailto:info@vaanabuildlogictechnologies.com" data-testid="contact-email" className="text-[#111827] font-medium text-sm hover:text-[#0D72B8] transition-colors break-all">info@vaanabuildlogictechnologies.com</a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <MapPin size={17} className="text-[#0D72B8] mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-[#9CA3AF] mb-0.5">Address</p>
                      <p data-testid="contact-address" className="text-[#111827] text-sm font-medium">Office No. 7 Ground Floor<br/>Indra Nursery, Near Badarpur Metro Station<br/>New Delhi 110044</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-7 bg-[#F9FAFB] border border-gray-200 rounded-2xl">
                <h4 className="text-sm font-semibold text-[#111827] mb-3">Business Hours</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-[#6B7280]">Mon - Fri</span><span className="text-[#111827] font-medium">9:00 AM - 6:00 PM</span></div>
                  <div className="flex justify-between"><span className="text-[#6B7280]">Saturday</span><span className="text-[#111827] font-medium">10:00 AM - 4:00 PM</span></div>
                  <div className="flex justify-between"><span className="text-[#6B7280]">Sunday</span><span className="text-[#9CA3AF]">Closed</span></div>
                </div>
              </div>

              <div className="p-7 bg-[#0D72B8] rounded-2xl text-center">
                <p className="text-white font-semibold text-sm mb-2">See how we help our clients win.</p>
                <p className="text-blue-200 text-xs mb-4">Call or email us now.</p>
                <p className="text-white text-lg font-medium mb-1">+91 9953167073</p>
                <p className="text-blue-200 text-xs">info@vaanabuildlogictechnologies.com</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
